import {
  Autocomplete,
  Box,
  IconButton,
  InputBaseComponentProps,
  MenuItem,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { FormikProps, getIn } from "formik";
import * as React from "react";
import { useState } from "react";
import { IMaskInput } from "react-imask";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { FormField } from "../FormField";
import { FieldMasks } from "../FieldMasks";
import { FormFieldTrigger } from "../FormFieldTrigger";
import { FixedLabel, maybePadRight } from "../Functions";
import { ClearButton } from "./ClearButton";

interface MaskProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

export const PhoneTextMask = React.forwardRef<HTMLInputElement, MaskProps>(
  function PhoneTextMask(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="(000) 000-0000"
        inputRef={ref}
        onChange={() => null}
        onAccept={(value) =>
          onChange({ target: { name: props.name, value: String(value) } })
        }
        overwrite
      />
    );
  },
);

export const SsnTextMask = React.forwardRef<HTMLInputElement, MaskProps>(
  function SsnTextMask(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="000-00-0000"
        inputRef={ref}
        onChange={() => null}
        onAccept={(value) =>
          onChange({ target: { name: props.name, value: String(value) } })
        }
        overwrite
      />
    );
  },
);

export function PhoneField(props: {
  formik: FormikProps<Record<string, unknown>>;
  field: FormField;
  classes: Record<string, string>;
  variant: "filled" | "outlined" | "standard";
}) {
  const { formik, field, classes, variant } = props;
  // getIn: inside a FieldArray, field.id is a path ("licenses[0].number"), which a flat
  // lookup cannot resolve — the clear button then never appeared for a populated field.
  const hasValue =
    getIn(formik.values, field.id) !== "" &&
    getIn(formik.values, field.id) != null;

  return (
    <Grid size={{ xs: 12, sm: field.columns || 6 }} key={field.id}>
      {field.fixedLabel && <FixedLabel formik={formik} field={field} />}
      <Box sx={{ display: "flex" }}>
        <Box sx={{ flexGrow: 1 }}>
          <TextField
            className={classes.formControl}
            id={field.id}
            label={!field.fixedLabel && field.text}
            variant={variant}
            fullWidth
            autoComplete=""
            value={getIn(formik.values, field.id) || ""}
            onChange={(e) =>
              formik.setFieldValue(field.id, e.target.value.replace(/\D/g, ""))
            }
            onBlur={formik.handleBlur}
            helperText={
              getIn(formik.touched, field.id) ? (
                <>{getIn(formik.errors, field.id) as string}</>
              ) : (
                ""
              )
            }
            error={
              getIn(formik.touched, field.id) &&
              Boolean(getIn(formik.errors, field.id))
            }
            slotProps={{
              input: {
                endAdornment:
                  field.showClearButton && hasValue ? (
                    <ClearButton
                      onClick={() => formik.setFieldValue(field.id, "")}
                    />
                  ) : null,
                inputComponent:
                  PhoneTextMask as unknown as React.ElementType<InputBaseComponentProps>,
              },
            }}
          />
        </Box>
      </Box>
    </Grid>
  );
}

export function SsnField(
  formik: FormikProps<Record<string, unknown>>,
  field: FormField,
  classes: Record<string, string>,
  variant: "filled" | "outlined" | "standard",
) {
  // getIn: inside a FieldArray, field.id is a path ("licenses[0].number"), which a flat
  // lookup cannot resolve — the clear button then never appeared for a populated field.
  const hasValue =
    getIn(formik.values, field.id) !== "" &&
    getIn(formik.values, field.id) != null;

  return (
    <Grid size={{ xs: 12, sm: field.columns || 6 }} key={field.id}>
      {field.fixedLabel && <FixedLabel formik={formik} field={field} />}
      <Box sx={{ display: "flex" }}>
        <Box sx={{ flexGrow: 1 }}>
          <TextField
            className={classes.formControl}
            id={field.id}
            label={!field.fixedLabel && field.text}
            variant={variant}
            fullWidth
            autoComplete=""
            value={getIn(formik.values, field.id) || ""}
            onChange={(e) =>
              formik.setFieldValue(field.id, e.target.value.replace(/\D/g, ""))
            }
            onBlur={formik.handleBlur}
            helperText={
              getIn(formik.touched, field.id) ? (
                <>{getIn(formik.errors, field.id) as string}</>
              ) : (
                ""
              )
            }
            error={
              getIn(formik.touched, field.id) &&
              Boolean(getIn(formik.errors, field.id))
            }
            slotProps={{
              input: {
                endAdornment:
                  field.showClearButton && hasValue ? (
                    <ClearButton
                      onClick={() => formik.setFieldValue(field.id, "")}
                    />
                  ) : null,
                inputComponent:
                  SsnTextMask as unknown as React.ElementType<InputBaseComponentProps>,
              },
            }}
          />
        </Box>
      </Box>
    </Grid>
  );
}

export function TextFieldWithoutMask(
  formik: FormikProps<Record<string, unknown>>,
  field: FormField,
  classes: Record<string, string>,
  variant: "filled" | "outlined" | "standard",
  triggers?: FormFieldTrigger[],
) {
  const [showPassword, setShowPassword] = useState(false);
  // getIn: inside a FieldArray, field.id is a path ("licenses[0].number"), which a flat
  // lookup cannot resolve — the clear button then never appeared for a populated field.
  const hasValue =
    getIn(formik.values, field.id) !== "" &&
    getIn(formik.values, field.id) != null;

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue(field.id, e.target.value);
    if (triggers) {
      triggers.forEach((trigger) => {
        if (trigger.sourceFieldId === field.id) {
          formik.setFieldValue(
            trigger.targetFieldId,
            trigger.valueChanger(e.target.value),
          );
        }
      });
    }
  };

  const optionalAttributes: {
    type?: string;
    disabled?: boolean;
    autoComplete?: string;
  } = {};
  if (field.password === true) {
    optionalAttributes.type = showPassword ? "text" : "password";
    optionalAttributes.autoComplete = field.autocomplete || "current-password";
  } else if (field.autocomplete) {
    optionalAttributes.autoComplete = field.autocomplete;
  }
  if (field.email === true) {
    optionalAttributes.type = "email";
    if (!optionalAttributes.autoComplete) optionalAttributes.autoComplete = "email";
  }
  // See FormField.email: phone keyboards must not capitalise or "correct" an address.
  const emailInputAttributes = field.email
    ? { inputMode: "email" as const, autoCapitalize: "none", autoCorrect: "off", spellCheck: false }
    : undefined;
  if (field.disabled === true) optionalAttributes.disabled = true;

  const getEndAdornment = () => {
    const elements = [];
    if (field.showClearButton && hasValue) {
      elements.push(
        <ClearButton
          key="clear"
          onClick={() => formik.setFieldValue(field.id, "")}
        />,
      );
    }
    if (field.password === true) {
      elements.push(
        <IconButton
          key="visibility"
          onClick={() => setShowPassword(!showPassword)}
          edge="end"
          size="small"
          sx={{ mr: 0.5 }}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>,
      );
    }
    return elements.length > 0 ? elements : null;
  };

  return (
    <React.Fragment key={field.id}>
      <Grid size={{ xs: 12, sm: field.columns || 6 }} key={field.id}>
        {field.fixedLabel && <FixedLabel formik={formik} field={field} />}
        <Box sx={{ display: "flex" }}>
          <Box sx={{ flexGrow: 1 }}>
            <TextField
              className={classes.formControl}
              id={field.id}
              label={!field.fixedLabel && field.text ? field.text : undefined}
              variant={variant}
              fullWidth
              {...optionalAttributes}
              value={getIn(formik.values, field.id) || ""}
              onChange={onChangeHandler}
              onBlur={formik.handleBlur}
              helperText={
                getIn(formik.touched, field.id) ? (
                  <>{getIn(formik.errors, field.id) as string}</>
                ) : (
                  ""
                )
              }
              error={
                getIn(formik.touched, field.id) &&
                Boolean(getIn(formik.errors, field.id))
              }
              slotProps={{
                input: { endAdornment: getEndAdornment() },
                htmlInput: emailInputAttributes,
              }}
            />
          </Box>
        </Box>
      </Grid>
      {maybePadRight(field)}
    </React.Fragment>
  );
}

export function MtcTextField(props: {
  formik: FormikProps<Record<string, unknown>>;
  field: FormField;
  classes: Record<string, string>;
  variant: "filled" | "outlined" | "standard";
  triggers?: FormFieldTrigger[];
}) {
  const { formik, field, classes, variant, triggers } = props;
  if (field.mask === FieldMasks.Phone) {
    return (
      <PhoneField
        formik={formik}
        field={field}
        classes={classes}
        variant={variant}
      />
    );
  }
  if (field.mask === FieldMasks.SSN) {
    return SsnField(formik, field, classes, variant);
  }
  return TextFieldWithoutMask(formik, field, classes, variant, triggers);
}

export function MultipleLineTextField(props: {
  formik: FormikProps<Record<string, unknown>>;
  field: FormField;
  classes: Record<string, string>;
  variant: "filled" | "outlined" | "standard";
}) {
  const { formik, field, classes, variant } = props;

  return (
    <Grid size={{ xs: 12, sm: field.columns || 6 }} key={field.id}>
      {field.fixedLabel && <FixedLabel formik={formik} field={field} />}
      <TextField
        className={classes.formControl}
        id={field.id}
        label={!field.fixedLabel && field.text}
        variant={variant}
        multiline
        rows={field.rows || undefined}
        fullWidth
        value={getIn(formik.values, field.id)}
        onChange={(e) => formik.setFieldValue(field.id, e.target.value)}
        onBlur={formik.handleBlur}
        helperText={
          getIn(formik.touched, field.id) ? (
            <>{getIn(formik.errors, field.id) as string}</>
          ) : (
            ""
          )
        }
        error={
          getIn(formik.touched, field.id) &&
          Boolean(getIn(formik.errors, field.id))
        }
      />
    </Grid>
  );
}

export function DropDownListField(props: {
  formik: FormikProps<Record<string, unknown>>;
  field: FormField;
  variant: "filled" | "outlined" | "standard";
  triggers?: FormFieldTrigger[];
  classes: Record<string, string>;
}) {
  const { formik, field, variant, triggers, classes } = props;
  const [selectOpen, setSelectOpen] = React.useState(false);
  // getIn: inside a FieldArray, field.id is a path ("licenses[0].number"), which a flat
  // lookup cannot resolve — the clear button then never appeared for a populated field.
  const hasValue =
    getIn(formik.values, field.id) !== "" &&
    getIn(formik.values, field.id) != null;

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue(field.id, e.target.value);
    if (triggers) {
      triggers.forEach((trigger) => {
        if (trigger.sourceFieldId === field.id) {
          formik.setFieldValue(
            trigger.targetFieldId,
            trigger.valueChanger(e.target.value),
          );
        }
      });
    }
  };

  return (
    <Grid size={{ xs: 12, sm: field.columns || 6 }}>
      {field.fixedLabel && <FixedLabel formik={formik} field={field} />}
      <Box sx={{ display: "flex" }}>
        <Box sx={{ flexGrow: 1 }}>
          <TextField
            name={field.id}
            className={classes.formControl}
            select
            slotProps={{
              select: {
                open: selectOpen,
                onOpen: () => setSelectOpen(true),
                onClose: () => setSelectOpen(false),
                IconComponent:
                  field.showClearButton && hasValue ? () => null : undefined,
              },
              input: {
                endAdornment:
                  field.showClearButton && hasValue ? (
                    <ClearButton
                      onClick={() => formik.setFieldValue(field.id, "")}
                    />
                  ) : null,
              },
            }}
            onKeyDown={(e) => {
              if (e.key) setSelectOpen(true);
            }}
            fullWidth
            id={field.id}
            label={!field.fixedLabel && field.text}
            variant={variant}
            value={getIn(formik.values, field.id) || ""}
            onChange={onChangeHandler}
            helperText={
              getIn(formik.touched, field.id) ? (
                <>{getIn(formik.errors, field.id) as string}</>
              ) : (
                ""
              )
            }
            error={
              getIn(formik.touched, field.id) &&
              Boolean(getIn(formik.errors, field.id))
            }
          >
            <MenuItem key={`${field.id}SelectOne`} value="">
              Select One
            </MenuItem>
            {field.choices?.map((choice, index) => (
              <MenuItem key={index} value={choice.value}>
                {choice.text}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
    </Grid>
  );
}

export function AutocompleteField(props: {
  formik: FormikProps<Record<string, unknown>>;
  field: FormField;
  variant: "filled" | "outlined" | "standard";
  classes: Record<string, string>;
}) {
  const { formik, field, variant, classes } = props;
  // getIn: inside a FieldArray, field.id is a path ("licenses[0].number"), which a flat
  // lookup cannot resolve — the clear button then never appeared for a populated field.
  const hasValue =
    getIn(formik.values, field.id) !== "" &&
    getIn(formik.values, field.id) != null;

  return (
    <React.Fragment key={field.id}>
      <Grid size={{ xs: 12, sm: field.columns || 6 }} key={field.id}>
        {field.fixedLabel && <FixedLabel formik={formik} field={field} />}
        <Box sx={{ display: "flex" }}>
          <Box sx={{ flexGrow: 1 }}>
            <Autocomplete
              value={getIn(formik.values, field.id) || null}
              onChange={(_e, newValue: string | null) =>
                formik.setFieldValue(field.id, newValue)
              }
              getOptionLabel={(option) =>
                (field.choices || []).find((x) => x.id === option)?.text || ""
              }
              isOptionEqualToValue={(option, value) => option === value}
              id={field.id}
              options={(field.choices || []).map((x) => x.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={classes.formControl}
                  label={!field.fixedLabel && field.text}
                  variant={variant}
                  fullWidth
                  onBlur={formik.handleBlur}
                  helperText={
                    getIn(formik.touched, field.id) ? (
                      <>{getIn(formik.errors, field.id) as string}</>
                    ) : (
                      ""
                    )
                  }
                  error={
                    getIn(formik.touched, field.id) &&
                    Boolean(getIn(formik.errors, field.id))
                  }
                  slotProps={{
                    ...params.slotProps,

                    input: {
                      endAdornment:
                        field.showClearButton && hasValue ? (
                          <ClearButton
                            onClick={() => formik.setFieldValue(field.id, null)}
                          />
                        ) : null,
                    },
                  }}
                />
              )}
            />
          </Box>
        </Box>
      </Grid>
      {maybePadRight(field)}
    </React.Fragment>
  );
}
